/**
 * AbaEmpresas — Diagnóstico Corporativo
 *
 * Visível EXCLUSIVAMENTE para a conta admin (Mônica).
 * Estrutura preparada para liberar para consultoras no futuro:
 * basta alterar a prop `podeAcessar` ao renderizar o componente.
 */
import { useState, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'
import {Plus, Copy, Trash2, FileText, Building2, X, AlertTriangle, Download, Lock} from 'lucide-react'
import { PERGUNTAS } from '@/types/diagnostico'
import jsPDF from 'jspdf'
import PhoneInput, { PAISES, validarNumero } from '@/components/PhoneInput'
import type { PaisOpcao } from '@/components/PhoneInput'
import { ASSINATURA } from '@/utils/assinatura'
import { copiarTexto } from '@/utils/clipboard'

// ---------------------------------------------------------------------------
// Utilitários
// ---------------------------------------------------------------------------
function gerarSlugDe(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Gera um slug único: se o slug base já existir na lista, adiciona
 * sufixo numérico sequencial (clinica-bem-viver, clinica-bem-viver-2, ...).
 */
function gerarSlugUnico(base: string, slugsExistentes: string[]): string {
  if (!base) return base
  if (!slugsExistentes.includes(base)) return base
  let n = 2
  while (slugsExistentes.includes(`${base}-${n}`)) n++
  return `${base}-${n}`
}

const BASE_URL = 'https://www.descubraseusoleos.com.br'

/**
 * Lê uma coleção inteira em páginas de 500, até não vir mais nada.
 * Evita qualquer corte de registros mantendo o limite por requisição.
 */
async function listarTudo(
  entidade: { list: (opcoes: any) => Promise<{ list?: any[] } | null | undefined> },
  opcoes: Record<string, any> = {},
): Promise<any[]> {
  const TAMANHO_PAGINA = 500
  const acumulado: any[] = []
  let skip = 0

  for (;;) {
    const res = await entidade.list({ ...opcoes, limit: TAMANHO_PAGINA, skip })
    const pagina: any[] = res?.list ?? []
    acumulado.push(...pagina)
    if (pagina.length < TAMANHO_PAGINA) break
    skip += TAMANHO_PAGINA
  }

  return acumulado
}

// ---------------------------------------------------------------------------
// Blocos temáticos — mapeamento de IDs por bloco
// ---------------------------------------------------------------------------
const BLOCOS_TEMATICOS = [
  { nome: 'Sono e Cansaço',              ids: ['sono', 'disposicao'] },
  { nome: 'Distração e Perda de Foco',   ids: ['foco', 'memoria'] },
  { nome: 'Ansiedade e Estresse',        ids: ['ansiedade', 'peso', 'dores', 'medicamentos'] },
  { nome: 'Desequilíbrios Físicos',      ids: ['inflamacoes', 'intestino'] },
  { nome: 'Instabilidade Emocional',     ids: ['vergonha', 'culpa', 'apatia', 'tristeza', 'medo', 'escassez', 'raiva', 'orgulho', 'estagnacao', 'desequilibrio'] },
]

/**
 * Calcula a média percentual de um bloco para um conjunto de leads.
 * Para cada respondente: (nº de SIM no bloco / total de perguntas do bloco) × 100
 * Média do bloco = soma das porcentagens individuais / nº de respondentes
 */
function calcularMediaBloco(leads: any[], ids: string[]): number {
  if (!leads.length || !ids.length) return 0
  const soma = leads.reduce((acc, lead) => {
    const r = lead.respostas || {}
    const sim = ids.filter(id => r[id] === true).length
    return acc + (sim / ids.length) * 100
  }, 0)
  return Math.round(soma / leads.length)
}

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
interface Empresa {
  _id: string
  nome: string
  slug: string
  responsavel?: string
  whatsappResponsavel?: string
  consultora: string
  createdAt: string
}

interface EmpresaForm {
  nome: string
  slug: string
  responsavel: string
  whatsappResponsavel: string
  paisResponsavel: PaisOpcao
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface Props {
  /** Usuário da consultora logada (ou 'monica' para admin) */
  consultoraAdmin: string
  /** Título profissional da Mônica para o relatório (vindo do perfil) */
  tituloProfissional?: string
  /** Se true, vê todas as empresas; se false, vê apenas as suas */
  isAdmin?: boolean
  /**
   * Liberar acesso para consultoras no futuro:
   * basta passar podeAcessar={true} ao renderizar este componente
   * para usuários com permissão.
   */
  podeAcessar?: boolean
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
interface AgregadoConsultora {
  usuario: string
  nome: string
  totalEmpresas: number
  totalDiagnosticos: number
  ultimoDiagnostico: string | null
}

export default function AbaEmpresas({ consultoraAdmin, tituloProfissional, isAdmin = false, podeAcessar = true }: Props) {
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [agregados, setAgregados] = useState<AgregadoConsultora[]>([])
  const [carregando, setCarregando] = useState(true)
  const [gerandoPDF, setGerandoPDF] = useState<string | null>(null)
  const [confirmarDelete, setConfirmarDelete] = useState<Empresa | null>(null)
  const [deletando, setDeletando] = useState(false)
  const [modalAberto, setModalAberto] = useState(false)
  const [form, setForm] = useState<EmpresaForm>({ nome: '', slug: '', responsavel: '', whatsappResponsavel: '', paisResponsavel: PAISES[0] })
  const [salvando, setSalvando] = useState(false)

  useEffect(() => {
    if (!podeAcessar) return
    if (isAdmin) {
      void carregarAgregadoAdmin()
    } else {
      void carregarEmpresas()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeAcessar, isAdmin])

  async function carregarEmpresas() {
    setCarregando(true)
    try {
      const res = await lumi.entities.empresas.list({ filter: { consultora: consultoraAdmin }, sort: { createdAt: -1 }, limit: 200 })
      setEmpresas(res?.list ?? [])
    } catch (e) {
      console.error('[empresas] erro ao carregar', e)
    } finally {
      setCarregando(false)
    }
  }

  async function carregarAgregadoAdmin() {
    setCarregando(true)
    try {
      const [listaEmpresasBruta, listaLeadsBruta, listaConsultoras] = await Promise.all([
        listarTudo(lumi.entities.empresas),
        listarTudo(lumi.entities.leads2, { sort: { data_hora: -1 } }),
        listarTudo(lumi.entities.consultoras),
      ])
      const listaEmpresas: any[] = listaEmpresasBruta
      const listaLeads: any[] = listaLeadsBruta.filter((l: any) => Boolean(l?.empresa))

      const nomePorUsuario: Record<string, string> = {}
      listaConsultoras.forEach((c: any) => {
        if (c?.usuario) nomePorUsuario[c.usuario] = c.nome || c.usuario
      })

      const map: Record<string, AgregadoConsultora> = {}
      listaEmpresas.forEach((emp: any) => {
        const usuario = emp?.consultora || 'geral'
        if (!map[usuario]) {
          map[usuario] = {
            usuario,
            nome: nomePorUsuario[usuario] || (usuario === 'geral' ? 'Sem consultora vinculada' : usuario),
            totalEmpresas: 0,
            totalDiagnosticos: 0,
            ultimoDiagnostico: null,
          }
        }
        map[usuario].totalEmpresas += 1
      })

      listaLeads.forEach((lead: any) => {
        const usuario = lead?.consultora || 'geral'
        if (!map[usuario]) {
          map[usuario] = {
            usuario,
            nome: nomePorUsuario[usuario] || (usuario === 'geral' ? 'Sem consultora vinculada' : usuario),
            totalEmpresas: 0,
            totalDiagnosticos: 0,
            ultimoDiagnostico: null,
          }
        }
        map[usuario].totalDiagnosticos += 1
        const data = lead?.data_hora || lead?.createdAt || null
        if (data && (!map[usuario].ultimoDiagnostico || data > map[usuario].ultimoDiagnostico)) {
          map[usuario].ultimoDiagnostico = data
        }
      })

      setAgregados(Object.values(map).sort((a, b) => b.totalEmpresas - a.totalEmpresas || b.totalDiagnosticos - a.totalDiagnosticos))
    } catch (e) {
      console.error('[empresas] erro ao carregar agregado', e)
      toast.error('Erro ao carregar o resumo corporativo')
    } finally {
      setCarregando(false)
    }
  }

  // ── Form helpers ──────────────────────────────────────────────────────────
  const handleNomeChange = (valor: string) => {
    setForm(prev => ({ ...prev, nome: valor, slug: gerarSlugDe(valor) }))
  }

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    const nome = form.nome.trim()
    const slugBase = form.slug.trim()
    if (!nome || !slugBase) { toast.error('Nome e slug são obrigatórios'); return }

    // Validar telefone do responsável apenas quando preenchido
    const numeroDigitado = (form.whatsappResponsavel || '').replace(/\D/g, '')
    if (numeroDigitado.length > 0) {
      const { valido, mensagem } = validarNumero(form.whatsappResponsavel, form.paisResponsavel)
      if (!valido) { toast.error(mensagem); return }
    }

    // Gerar slug único automaticamente (evita erro por duplicidade)
    const slugsExistentes = empresas.map(emp => emp.slug)
    const slug = gerarSlugUnico(slugBase, slugsExistentes)

    // Montar WhatsApp com código do país (quando preenchido)
    const whatsappCompleto = numeroDigitado.length > 0
      ? `${form.paisResponsavel.codigo} ${numeroDigitado}`.trim()
      : ''

    setSalvando(true)
    try {
      const payload = {
        nome,
        slug,
        responsavel: form.responsavel.trim() || '',
        whatsappResponsavel: whatsappCompleto || '',
        consultora: consultoraAdmin,
      }
      console.log('[empresas] payload create:', JSON.stringify(payload))
      const result = await lumi.entities.empresas.create(payload)
      console.log('[empresas] create result:', JSON.stringify(result))
      toast.success(`Empresa "${nome}" cadastrada!`)
      setForm({ nome: '', slug: '', responsavel: '', whatsappResponsavel: '', paisResponsavel: PAISES[0] })
      setModalAberto(false)
      await carregarEmpresas()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('[empresas] erro ao salvar — detalhes:', msg, err)
      toast.error(`Erro ao cadastrar empresa: ${msg}`)
    } finally {
      setSalvando(false)
    }
  }

  const copiarLink = async (empresa: Empresa) => {
    const link = `${BASE_URL}/corporativo?org=${empresa.slug}&c=${empresa.consultora}`
    try { await copiarTexto(link); toast.success('Link copiado!') }
    catch { toast.error('Erro ao copiar link') }
  }

  const deletarEmpresa = async () => {
    if (!confirmarDelete) return
    setDeletando(true)
    try {
      await lumi.entities.empresas.delete(confirmarDelete._id)
      toast.success(`Empresa "${confirmarDelete.nome}" excluída.`)
      setConfirmarDelete(null)
      await carregarEmpresas()
    } catch (err) {
      console.error('[empresas] erro ao deletar', err)
      toast.error('Erro ao excluir empresa')
    } finally {
      setDeletando(false)
    }
  }

  // ── Geração do PDF ────────────────────────────────────────────────────────
  const gerarRelatorioPDF = async (empresa: Empresa) => {
    setGerandoPDF(empresa._id)
    try {
      // Carregar leads vinculados a esta empresa
      const leads: any[] = await listarTudo(lumi.entities.leads2, { filter: { empresa: empresa.slug } })
      const total = leads.length

      if (total === 0) { toast.error('Nenhum respondente encontrado para esta empresa.'); setGerandoPDF(null); return }

      // Calcular média de cada bloco temático
      const mediasBlocos = BLOCOS_TEMATICOS.map(bloco => ({
        nome: bloco.nome,
        pct: calcularMediaBloco(leads, bloco.ids),
      }))

      // Determinar datas do período
      const datas = leads.map(l => l.data_hora).filter(Boolean).sort()
      const dataInicio = datas[0] ? new Date(datas[0]).toLocaleDateString('pt-BR') : '—'
      const dataFim = datas[datas.length - 1] ? new Date(datas[datas.length - 1]).toLocaleDateString('pt-BR') : '—'

      // ── Montar PDF ──────────────────────────────────────────────────────
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const W = 210, H = 297
      const margem = 20
      const verde = '#2D6A4F'
      const begeEscuro = '#B08968'
      const textoEscuro = '#2C2C2A'
      const textoSuave = '#5F5E5A'
      const begeClaro = '#F8F4EF'
      let y = 0

      const novaPagina = () => {
        doc.addPage()
        y = margem
        // Rodapé
        doc.setFontSize(8)
        doc.setTextColor(textoSuave)
        doc.text('Mônica Maria — Reacenda Luz  |  Sistema Descubra seus Óleos', W / 2, H - 10, { align: 'center' })
      }

      const verificarQuebraLinha = (altura: number) => {
        if (y + altura > H - 20) novaPagina()
      }

      // Rodapé página 1
      doc.setFontSize(8)
      doc.setTextColor(textoSuave)
      doc.text('Mônica Maria — Reacenda Luz  |  Sistema Descubra seus Óleos', W / 2, H - 10, { align: 'center' })

      // ── CAPA ─────────────────────────────────────────────────────────────
      // Fundo verde no topo
      doc.setFillColor(verde)
      doc.rect(0, 0, W, 80, 'F')

      doc.setFontSize(11)
      doc.setTextColor('#FFFFFF')
      doc.setFont('helvetica', 'bold')
      doc.text('SISTEMA DESCUBRA SEUS ÓLEOS', W / 2, 22, { align: 'center' })

      doc.setFontSize(22)
      doc.setFont('times', 'bold')
      doc.text(empresa.nome, W / 2, 38, { align: 'center' })

      doc.setFontSize(13)
      doc.setFont('helvetica', 'normal')
      doc.text('Relatório de Saúde e Bem-Estar da Equipe', W / 2, 50, { align: 'center' })

      doc.setFontSize(9)
      doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')}`, W / 2, 60, { align: 'center' })

      y = 90

      // Caixas de destaque
      const boxW = (W - margem * 2 - 8) / 2
      const boxH = 28

      // Caixa total
      doc.setFillColor(begeClaro)
      doc.roundedRect(margem, y, boxW, boxH, 4, 4, 'F')
      doc.setFontSize(24)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(verde)
      doc.text(String(total), margem + boxW / 2, y + 12, { align: 'center' })
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(textoSuave)
      doc.text('Total de participantes', margem + boxW / 2, y + 20, { align: 'center' })

      // Caixa período
      doc.setFillColor(begeClaro)
      doc.roundedRect(margem + boxW + 8, y, boxW, boxH, 4, 4, 'F')
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(textoEscuro)
      doc.text(`${dataInicio} — ${dataFim}`, margem + boxW + 8 + boxW / 2, y + 12, { align: 'center' })
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(textoSuave)
      doc.text('Período da coleta', margem + boxW + 8 + boxW / 2, y + 20, { align: 'center' })

      y += boxH + 16

      // Preparado por
      doc.setFontSize(9)
      doc.setTextColor(textoSuave)
      doc.text(`Preparado por: Mônica Maria${tituloProfissional ? ` — ${tituloProfissional}` : ''}`, margem, y)
      y += 16

      // ── SEÇÃO 1 — Panorama ────────────────────────────────────────────────
      doc.setFillColor(verde)
      doc.rect(margem, y, W - margem * 2, 10, 'F')
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor('#FFFFFF')
      doc.text('SEÇÃO 1 — PANORAMA GERAL', margem + 4, y + 7)
      y += 16

      doc.setFontSize(10)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(textoEscuro)
      doc.text(`Total de respondentes: ${total}`, margem, y); y += 7
      doc.text(`Período da coleta: de ${dataInicio} até ${dataFim}`, margem, y); y += 14

      // ── SEÇÃO 2 — Saúde física e emocional ───────────────────────────────
      verificarQuebraLinha(80)
      doc.setFillColor(verde)
      doc.rect(margem, y, W - margem * 2, 10, 'F')
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor('#FFFFFF')
      doc.text('SEÇÃO 2 — SAÚDE FÍSICA E EMOCIONAL', margem + 4, y + 7)
      y += 16

      const desenharBarra = (label: string, pct: number, cor: string) => {
        verificarQuebraLinha(22)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(textoEscuro)
        const labelStr = label.length > 72 ? label.slice(0, 72) + '…' : label
        doc.text(labelStr, margem, y)
        y += 5

        // Trilho
        doc.setFillColor('#EDE0D4')
        doc.roundedRect(margem, y, W - margem * 2 - 25, 5, 2, 2, 'F')
        // Barra
        if (pct > 0) {
          doc.setFillColor(cor)
          doc.roundedRect(margem, y, Math.max(2, ((W - margem * 2 - 25) * pct) / 100), 5, 2, 2, 'F')
        }
        // Percentual
        doc.setFontSize(8)
        doc.setTextColor(textoEscuro)
        doc.text(`${pct}%`, W - margem - 20, y + 4)
        y += 12
      }

      // Legenda dos blocos
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(textoSuave)
      const legendaTxt = 'Média percentual por bloco: (respostas SIM do bloco ÷ perguntas do bloco) × 100, calculada por respondente e depois pela equipe.'
      const legendaLinhas = doc.splitTextToSize(legendaTxt, W - margem * 2)
      doc.text(legendaLinhas, margem, y)
      y += legendaLinhas.length * 5 + 8

      // Desenhar os 5 blocos temáticos
      const coresBlocos = [verde, '#52B788', '#B08968', verde, '#52B788']
      mediasBlocos.forEach((bloco, i) => {
        desenharBarra(bloco.nome, bloco.pct, coresBlocos[i % coresBlocos.length])
      })

      // ── SEÇÃO 3 — Recomendações ───────────────────────────────────────────
      verificarQuebraLinha(80)
      doc.setFillColor(verde)
      doc.rect(margem, y, W - margem * 2, 10, 'F')
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor('#FFFFFF')
      doc.text('SEÇÃO 3 — RECOMENDAÇÕES GERAIS', margem + 4, y + 7)
      y += 16

      // Identificar os 2 blocos com maior média
      const blocosOrdenados = [...mediasBlocos].sort((a, b) => b.pct - a.pct)
      const topBloco1 = blocosOrdenados[0]
      const topBloco2 = blocosOrdenados[1]

      const recomendacoes = [
        topBloco1 && topBloco1.pct > 0
          ? `Identificamos que o bloco "${topBloco1.nome}" apresentou a maior média da equipe (${topBloco1.pct}%), sendo o principal ponto de atenção neste diagnóstico.`
          : null,
        topBloco2 && topBloco2.pct > 0
          ? `Em seguida, o bloco "${topBloco2.nome}" também merece atenção, com média de ${topBloco2.pct}% entre os respondentes.`
          : null,
        `Sugestões de ações:`,
        `  1. Difusão de óleos no ambiente: difusores com óleos essenciais cítricos ou misturas energizantes para promover foco, otimismo e bem-estar coletivo no dia a dia. Alguns óleos indicados: limão siciliano, laranja, eucalipto, mix respiratório, mix revigorante e mix calmante.`,
        `  2. Devolutiva individual com escuta ativa: cada colaborador recebe um momento dedicado para compartilhar como está se sentindo e receber seu protocolo personalizado de óleos, de forma privada e acolhedora.`,
        `  3. Encontros mensais de bem-estar: reavaliação periódica da equipe para acompanhar evolução dos indicadores e ajustar o protocolo coletivo.`,
        `  4. Vivências de bem-estar: experiências práticas com óleos essenciais para a equipe, promovendo relaxamento e conexão no ambiente de trabalho.`,
        `  5. Palestras sobre aromaterapia: encontros educativos para a equipe entender como os óleos essenciais podem apoiar a saúde física e emocional no dia a dia.`,
      ].filter(Boolean) as string[]

      recomendacoes.forEach(linha => {
        verificarQuebraLinha(16)
        const linhas = doc.splitTextToSize(linha, W - margem * 2)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(textoEscuro)
        doc.text(linhas, margem, y)
        y += linhas.length * 5 + 4
      })

      // ── SEÇÃO 4 — Metodologia ─────────────────────────────────────────────
      verificarQuebraLinha(80)
      doc.setFillColor(verde)
      doc.rect(margem, y, W - margem * 2, 10, 'F')
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor('#FFFFFF')
      doc.text('SEÇÃO 4 — METODOLOGIA E CONFIDENCIALIDADE', margem + 4, y + 7)
      y += 16

      const metodologia = [
        'Este relatório foi gerado com base nas respostas anônimas coletadas pelo Diagnóstico de Saúde e Emoções, uma ferramenta de autoconhecimento que avalia 20 indicadores de saúde física e emocional por meio de perguntas objetivas (Sim / Não).',
        'Garantia de confidencialidade: nenhuma resposta individual foi identificada. Os dados apresentados neste relatório são exclusivamente agregados, garantindo total privacidade a cada colaborador.',
        'As recomendações apresentadas têm caráter informativo e educativo, baseadas em práticas de aromaterapia com óleos essenciais. Não substituem orientação médica ou psicológica.',
      ]

      metodologia.forEach(paragrafo => {
        verificarQuebraLinha(20)
        const linhas = doc.splitTextToSize(paragrafo, W - margem * 2)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(textoEscuro)
        doc.text(linhas, margem, y)
        y += linhas.length * 5 + 6
      })

      // Assinatura
      y += 10
      verificarQuebraLinha(30)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(verde)
      doc.text('Mônica Maria', margem, y); y += 5
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(textoSuave)
      if (tituloProfissional) { doc.text(tituloProfissional, margem, y); y += 5 }
      doc.text('Reacenda Luz — Sistema Descubra seus Óleos', margem, y)

      // Salvar
      doc.save(`relatorio-${empresa.slug}-${new Date().toISOString().split('T')[0]}.pdf`)
      toast.success('Relatório gerado com sucesso!')
    } catch (err) {
      console.error('[empresas] erro ao gerar PDF', err)
      toast.error('Erro ao gerar relatório')
    } finally {
      setGerandoPDF(null)
    }
  }

  // ── JSX ────────────────────────────────────────────────────────────────────
  if (!podeAcessar) {
    return (
      <div className="bg-white p-8 sm:p-10 rounded-2xl shadow-md text-center max-w-lg mx-auto">
        <div className="w-14 h-14 rounded-full bg-[#FAEEDA] flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-[#854F0B]" />
        </div>
        <h3 className="text-xl font-cormorant font-semibold text-texto-escuro mb-2">
          Esta funcionalidade é exclusiva da assinatura anual.
        </h3>
        <p className="text-texto-suave text-sm leading-relaxed mb-6">
          Assine para ter acesso à área Empresas com contratos e relatórios.
        </p>
        <div className="flex flex-col gap-3">
          <a
            href={ASSINATURA.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-5 py-3 rounded-full bg-[#2D6A4F] text-white text-sm font-semibold hover:bg-[#245A42] transition-colors"
          >
            {ASSINATURA.botao} →
          </a>
        </div>
      </div>
    )
  }

  if (isAdmin) {
    const totalEmpresas = agregados.reduce((acc, item) => acc + item.totalEmpresas, 0)
    const totalDiagnosticos = agregados.reduce((acc, item) => acc + item.totalDiagnosticos, 0)
    return (
      <>
        <div className="bg-[#FEF9E7] border border-[#F5E1A4] rounded-2xl p-4 mb-6 text-sm text-[#7A5C00] leading-relaxed flex items-start gap-2">
          <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Por privacidade, a administradora visualiza apenas totais agregados por consultora. Nomes de empresas, responsáveis, WhatsApp, links do diagnóstico corporativo e relatórios individuais não são exibidos aqui.</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white p-5 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-verde-escuro mb-1">{agregados.length}</div>
            <div className="text-sm text-texto-suave">Consultoras com atividade corporativa</div>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-texto-escuro mb-1">{totalEmpresas}</div>
            <div className="text-sm text-texto-suave">Total de empresas cadastradas</div>
          </div>
          <div className="bg-white p-5 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-600 mb-1">{totalDiagnosticos}</div>
            <div className="text-sm text-texto-suave">Diagnósticos corporativos</div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-bege-medio">
            <h2 className="text-lg font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
              <Building2 className="w-5 h-5 text-verde-escuro" />
              Diagnóstico corporativo por consultora
            </h2>
          </div>
          {carregando ? (
            <div className="p-8 text-center text-texto-suave">Carregando...</div>
          ) : agregados.length === 0 ? (
            <div className="p-8 text-center text-texto-suave">Nenhuma atividade corporativa registrada ainda.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-bege-fundo">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Consultora</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Empresas cadastradas</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Diagnósticos corporativos</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-texto-escuro">Último diagnóstico</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bege-medio">
                  {agregados.map(item => (
                    <tr key={item.usuario} className="hover:bg-bege-fundo">
                      <td className="px-4 py-3 text-sm text-texto-escuro font-medium">{item.nome}</td>
                      <td className="px-4 py-3 text-sm text-texto-escuro font-semibold">{item.totalEmpresas}</td>
                      <td className="px-4 py-3 text-sm text-blue-600">{item.totalDiagnosticos}</td>
                      <td className="px-4 py-3 text-sm text-texto-suave">
                        {item.ultimoDiagnostico ? new Date(item.ultimoDiagnostico).toLocaleDateString('pt-BR') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {/* ── Bloco download proposta ── */}
      <div className="bg-white border border-[#EDE0D4] rounded-2xl p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#D8F3DC] flex items-center justify-center">
          <Download className="w-6 h-6 text-[#2D6A4F]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-[#2C2C2A] mb-1">Modelos de Propostas Corporativas</h3>
          <p className="text-sm text-[#5F5E5A] leading-relaxed">
            Confira os modelos de propostas disponíveis para apresentar o Diagnóstico Corporativo para novas empresas e personalize os dados antes de enviar.
          </p>
        </div>
        <a
          href="https://drive.google.com/drive/folders/1ZTddeyrfOCThs4D_tMo_VK4dPkjOaUAY?usp=sharing"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 flex items-center gap-2 bg-[#2D6A4F] text-white px-5 py-2.5 rounded-xl hover:bg-[#245A42] transition-all font-medium text-sm whitespace-nowrap"
        >
          Ver modelos de propostas →
        </a>
      </div>

      {/* ── Bloco explicativo ── */}
      <div className="bg-[#D8F3DC] border border-[#52B788] rounded-2xl p-6 mb-8">
        <h2 className="text-xl font-cormorant font-semibold text-[#2D6A4F] mb-3 flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Por que fazer o Diagnóstico Corporativo?
        </h2>
        <p className="text-sm text-[#2C2C2A] leading-relaxed mb-4">
          O diagnóstico corporativo é uma forma poderosa de entrar em empresas, oferecer bem-estar para equipes e se posicionar como consultora de saúde. Com ele você:
        </p>
        <ol className="space-y-4 text-sm text-[#2C2C2A]">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2D6A4F] text-white flex items-center justify-center font-bold text-xs">1</span>
            <div>
              <strong className="text-[#2D6A4F]">Cria um link exclusivo para a empresa.</strong>{' '}
              Cada empresa recebe um link personalizado para seus funcionários responderem o diagnóstico de forma anônima e segura.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2D6A4F] text-white flex items-center justify-center font-bold text-xs">2</span>
            <div>
              <strong className="text-[#2D6A4F]">Coleta as respostas da equipe.</strong>{' '}
              Os funcionários preenchem o diagnóstico individualmente e recebem seu protocolo personalizado de óleos essenciais. Cada funcionário que preencher ficará identificado no seu painel de Contatos para acompanhamento.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2D6A4F] text-white flex items-center justify-center font-bold text-xs">3</span>
            <div>
              <strong className="text-[#2D6A4F]">Gera um relatório agregado.</strong>{' '}
              Ao final você gera um relatório completo de saúde e bem-estar da equipe para entregar à empresa, com os principais indicadores e sugestões de ação.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2D6A4F] text-white flex items-center justify-center font-bold text-xs">4</span>
            <div>
              <strong className="text-[#2D6A4F]">Abre portas para novos serviços.</strong>{' '}
              Com o relatório em mãos a empresa pode contratar devolutivas individuais, encontros mensais, vivências e palestras de aromaterapia.
            </div>
          </li>
        </ol>
      </div>

      {/* Cabeçalho + botão novo */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
            <Building2 className="w-6 h-6 text-verde-escuro" />
            Diagnóstico Corporativo — Empresas
          </h2>
          <p className="text-sm text-texto-suave mt-1">
            Cadastre empresas, gere links personalizados e relatórios agregados de saúde da equipe.
          </p>
        </div>
        <button
          onClick={() => setModalAberto(true)}
          className="flex items-center gap-2 bg-verde-escuro text-white px-5 py-2.5 rounded-xl hover:bg-opacity-90 transition-all font-medium text-sm flex-shrink-0"
        >
          <Plus className="w-4 h-4" />
          Nova empresa
        </button>
      </div>

      {/* Lista de empresas */}
      {carregando ? (
        <div className="bg-white p-8 rounded-xl shadow-md text-center text-texto-suave">Carregando...</div>
      ) : empresas.length === 0 ? (
        <div className="bg-white p-10 rounded-xl shadow-md text-center">
          <Building2 className="w-10 h-10 text-texto-suave mx-auto mb-3" />
          <p className="text-texto-suave mb-1">Nenhuma empresa cadastrada ainda.</p>
          <p className="text-sm text-texto-suave">Clique em "Nova empresa" para começar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {empresas.map(emp => {
            const link = `${BASE_URL}/corporativo?org=${emp.slug}&c=${emp.consultora}`
            return (
              <div key={emp._id} className="bg-white rounded-xl shadow-md p-6">
                <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="text-lg font-cormorant font-semibold text-texto-escuro">{emp.nome}</h3>
                      <span className="px-2 py-0.5 bg-[#D8F3DC] text-[#2D6A4F] rounded-full text-xs font-medium">/{emp.slug}</span>
                    </div>
                    {emp.responsavel && (
                      <p className="text-sm text-texto-suave">Responsável: {emp.responsavel}{emp.whatsappResponsavel ? ` · ${emp.whatsappResponsavel}` : ''}</p>
                    )}
                    <p className="text-xs text-texto-suave mt-1">
                      {emp.createdAt ? `Cadastrada em ${new Date(emp.createdAt).toLocaleDateString('pt-BR')}` : ''}
                    </p>
                    {/* Link */}
                    <div className="mt-3 flex items-center gap-2 bg-bege-fundo border border-bege-medio rounded-xl px-3 py-2">
                      <span className="text-xs text-texto-suave truncate flex-1">{link}</span>
                      <button
                        onClick={() => copiarLink(emp)}
                        className="flex items-center gap-1 text-xs text-verde-escuro hover:text-verde-claro transition-colors flex-shrink-0 font-medium"
                      >
                        <Copy className="w-3.5 h-3.5" />
                        Copiar link
                      </button>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => gerarRelatorioPDF(emp)}
                      disabled={gerandoPDF === emp._id}
                      className="flex items-center gap-2 px-4 py-2 bg-[#2D6A4F] text-white rounded-xl text-sm font-medium hover:bg-[#245A42] transition-all disabled:opacity-60"
                    >
                      <FileText className="w-4 h-4" />
                      {gerandoPDF === emp._id ? 'Gerando...' : 'Gerar relatório'}
                    </button>
                    <button
                      onClick={() => setConfirmarDelete(emp)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Excluir empresa"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal cadastro */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-bege-medio">
              <h2 className="text-xl font-cormorant font-semibold text-texto-escuro flex items-center gap-2">
                <Building2 className="w-5 h-5 text-verde-escuro" />
                Cadastrar nova empresa
              </h2>
              <button onClick={() => setModalAberto(false)} className="text-texto-suave hover:text-texto-escuro">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSalvar} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-texto-suave mb-1 font-medium">Nome da empresa *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={e => handleNomeChange(e.target.value)}
                  placeholder="Ex: Clínica Bem Viver"
                  required
                  className="w-full px-4 py-2.5 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-texto-suave mb-1 font-medium">Slug (gerado automaticamente, editável) *</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={e => setForm(prev => ({ ...prev, slug: gerarSlugDe(e.target.value) }))}
                  placeholder="ex: clinica-bem-viver"
                  required
                  className="w-full px-4 py-2.5 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none font-mono text-sm"
                />
                {form.slug && (
                  <p className="text-xs text-texto-suave mt-1">
                    Link: {BASE_URL}/corporativo?org=<strong>{form.slug}</strong>&c={consultoraAdmin}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-texto-suave mb-1 font-medium">Responsável / contato (uso interno)</label>
                <input
                  type="text"
                  value={form.responsavel}
                  onChange={e => setForm(prev => ({ ...prev, responsavel: e.target.value }))}
                  placeholder="Nome do contato na empresa"
                  className="w-full px-4 py-2.5 border-2 border-bege-medio rounded-xl focus:border-verde-claro focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-texto-suave mb-1 font-medium">WhatsApp do responsável (uso interno)</label>
                <PhoneInput
                  value={form.whatsappResponsavel}
                  paisInicial={form.paisResponsavel}
                  onChange={(numero, pais) =>
                    setForm(prev => ({ ...prev, whatsappResponsavel: numero, paisResponsavel: pais }))
                  }
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalAberto(false)}
                  className="flex-1 px-4 py-3 border-2 border-bege-medio text-texto-escuro rounded-xl hover:bg-bege-fundo transition-colors font-medium">
                  Cancelar
                </button>
                <button type="submit" disabled={salvando}
                  className="flex-1 px-4 py-3 bg-verde-escuro text-white rounded-xl hover:bg-opacity-90 transition-colors font-medium disabled:opacity-60">
                  {salvando ? 'Salvando...' : 'Cadastrar empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal confirmação de exclusão */}
      {confirmarDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-cormorant font-semibold text-texto-escuro">
                Excluir "{confirmarDelete.nome}"?
              </h3>
            </div>
            <p className="text-texto-suave text-sm mb-6 leading-relaxed">
              Isso removerá o cadastro da empresa. Os leads já coletados <strong>não serão excluídos</strong> — apenas a empresa deixará de aparecer aqui. Ação irreversível.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmarDelete(null)} disabled={deletando}
                className="flex-1 px-4 py-3 border-2 border-bege-medio text-texto-escuro rounded-xl hover:bg-bege-fundo transition-colors font-medium disabled:opacity-60">
                Cancelar
              </button>
              <button onClick={deletarEmpresa} disabled={deletando}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                <Trash2 className="w-4 h-4" />
                {deletando ? 'Excluindo...' : 'Sim, excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
